import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Search, PackagePlus, SlidersHorizontal, AlertTriangle, Boxes, History, Coins } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money, fecha, norm } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const hoy = () => new Date().toISOString().slice(0, 10);

const articuloVacio = {
  codigo: "", nombre: "", categoria: "Uniformes", talla: "", grado: "", costo: "",
  precio: "", existencia_inicial: 0, minimo: 0, proveedor: "", notas: "", activo: true,
};

const TALLAS = ["XS", "S", "M", "L", "XL", "2", "4", "6", "8", "10", "12", "14", "16"];

export default function AdminInventario() {
  const [datos, setDatos] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [soloAlertas, setSoloAlertas] = useState(false);
  const [form, setForm] = useState(null);
  const [entrada, setEntrada] = useState(null);
  const [ajuste, setAjuste] = useState(null);
  const [movimientos, setMovimientos] = useState(null);

  const cargar = () => api.get("/inventario", { params: { incluir_inactivos: true } })
    .then((r) => setDatos(r.data)).catch(() => setDatos({ articulos: [], resumen: {}, categorias: [] }));

  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre del artículo");
    if (!form.precio || Number(form.precio) <= 0) return toast.error("Indique el precio de venta");
    const payload = {
      ...form, codigo: form.codigo.trim() || null, talla: form.talla || null,
      grado: form.grado || null, proveedor: form.proveedor || null, notas: form.notas || null,
      costo: Number(form.costo || 0), precio: Number(form.precio),
      existencia_inicial: Number(form.existencia_inicial || 0), minimo: Number(form.minimo || 0),
    };
    try {
      if (form.id) await api.put(`/inventario/${form.id}`, payload);
      else await api.post("/inventario", payload);
      toast.success(form.id ? "Artículo actualizado" : "Artículo agregado al inventario");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarEntrada = async () => {
    if (!entrada.cantidad || Number(entrada.cantidad) <= 0) return toast.error("Indique la cantidad recibida");
    try {
      const { data } = await api.post(`/inventario/${entrada.articulo.id}/entrada`, {
        cantidad: Number(entrada.cantidad),
        costo_unitario: entrada.costo_unitario === "" ? null : Number(entrada.costo_unitario),
        proveedor: entrada.proveedor || null,
        fecha: new Date(entrada.fecha).toISOString(),
        documento: entrada.documento || null,
        registrar_gasto: entrada.registrar_gasto,
        metodo_pago: entrada.metodo_pago,
      });
      toast.success(`Entrada registrada: ${data.articulo.nombre} queda con ${data.articulo.existencia}`,
        { description: data.gasto_id ? "Se registró también el gasto en la categoría Inventario." : undefined });
      setEntrada(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const guardarAjuste = async () => {
    if (!ajuste.motivo.trim()) return toast.error("Indique el motivo del ajuste");
    try {
      const { data } = await api.post(`/inventario/${ajuste.articulo.id}/ajuste`, {
        existencia: Number(ajuste.existencia), motivo: ajuste.motivo,
      });
      toast.success(`Existencia ajustada de ${data.anterior} a ${data.articulo.existencia}`);
      setAjuste(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (a) => {
    try {
      const { data } = await api.delete(`/inventario/${a.id}`);
      toast.success(data.desactivado ? data.detalle : `${a.nombre} eliminado del inventario`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const verMovimientos = async (a) => {
    try {
      const { data } = await api.get(`/inventario/${a.id}/movimientos`);
      setMovimientos({ articulo: a, rows: data });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!datos) return <Loading />;

  const { articulos, resumen, categorias } = datos;
  const q = norm(busqueda.trim());
  const visibles = articulos.filter((a) =>
    (filtro === "todas" || a.categoria === filtro) &&
    (!soloAlertas || a.existencia <= a.minimo) &&
    (!q || [a.codigo, a.nombre, a.talla, a.grado, a.proveedor].filter(Boolean).some((v) => norm(v).includes(q))));

  return (
    <div data-testid="admin-inventario">
      <PageTitle title="Inventario"
                 subtitle="Uniformes, libros y materiales con existencia, costo y precio de venta. Al registrar una venta se toma el precio y se descuenta la existencia.">
        <Button className="rounded-full" data-testid="nuevo-articulo"
                onClick={() => setForm({ ...articuloVacio, categoria: categorias[0] || "Uniformes" })}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo artículo
        </Button>
      </PageTitle>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="inv-articulos" label="Artículos activos" value={resumen.articulos || 0}
              icon={Boxes} hint={`${resumen.unidades || 0} unidades en existencia`} />
        <Stat testId="inv-costo" label="Valor a costo" value={money(resumen.valor_costo || 0)}
              icon={Coins} hint="Lo invertido en mercancía" />
        <Stat testId="inv-venta" label="Valor a precio de venta" value={money(resumen.valor_venta || 0)}
              tone="good" icon={Coins} hint="Si se vendiera todo" />
        <Stat testId="inv-alertas" label="Por reponer" value={(resumen.bajo_minimo || 0) + (resumen.agotados || 0)}
              tone={(resumen.agotados || 0) > 0 ? "bad" : "warn"} icon={AlertTriangle}
              hint={`${resumen.agotados || 0} agotado(s) · ${resumen.bajo_minimo || 0} bajo el mínimo`} />
      </div>

      {(resumen.alertas || []).length > 0 && (
        <div className="mb-8 rounded-lg border border-accent/40 bg-accent/5 p-5" data-testid="panel-alertas-inventario">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 text-accent-foreground" /> Hay que reponer
          </p>
          <div className="flex flex-wrap gap-2">
            {resumen.alertas.map((a) => (
              <span key={a.id} data-testid={`alerta-${a.codigo}`}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      a.existencia <= 0 ? "border-destructive/40 text-destructive" : "border-border"}`}>
                {a.nombre}{a.talla ? ` · ${a.talla}` : ""}: {a.existencia} de {a.minimo} mín.
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="rounded-full pl-10" placeholder="Buscar por código, nombre, talla o grado…"
                 value={busqueda} onChange={(e) => setBusqueda(e.target.value)} data-testid="buscar-articulo" />
        </div>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="filtro-categoria-inv"
                className="rounded-full border border-border bg-card px-4 py-2 text-sm">
          <option value="todas">Todas las categorías</option>
          {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button size="sm" variant={soloAlertas ? "default" : "outline"} className="rounded-full"
                onClick={() => setSoloAlertas(!soloAlertas)} data-testid="filtro-por-reponer">
          Por reponer
        </Button>
        <span className="text-xs text-muted-foreground" data-testid="contador-articulos">
          {visibles.length} de {articulos.length}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="tabla-inventario">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Código</th>
                <th className="px-6 py-3">Artículo</th>
                <th className="px-6 py-3">Categoría</th>
                <th className="px-6 py-3 text-right">Costo</th>
                <th className="px-6 py-3 text-right">Precio</th>
                <th className="px-6 py-3 text-right">Existencia</th>
                <th className="px-6 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((a) => (
                <tr key={a.id} className={`border-t border-border/60 hover:bg-muted/20 ${a.activo === false ? "opacity-50" : ""}`}
                    data-testid={`articulo-${a.codigo}`}>
                  <td className="px-6 py-4 font-mono text-xs">{a.codigo}</td>
                  <td className="px-6 py-4">
                    <p className="font-medium">{a.nombre}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.talla ? `Talla ${a.talla}` : ""}{a.talla && a.grado ? " · " : ""}{a.grado || ""}
                      {a.proveedor ? `${a.talla || a.grado ? " · " : ""}${a.proveedor}` : ""}
                      {a.activo === false ? " · inactivo" : ""}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{a.categoria}</td>
                  <td className="px-6 py-4 text-right font-mono">{money(a.costo || 0)}</td>
                  <td className="px-6 py-4 text-right font-mono font-semibold">{money(a.precio)}</td>
                  <td className="px-6 py-4 text-right">
                    <span className={`rounded-full px-2.5 py-1 font-mono text-xs ${
                      a.existencia <= 0 ? "bg-destructive/10 text-destructive"
                        : a.existencia <= a.minimo ? "bg-accent/20 text-accent-foreground"
                        : "bg-muted text-muted-foreground"}`} data-testid={`existencia-${a.codigo}`}>
                      {a.existencia}
                    </span>
                    <span className="ml-2 text-[11px] text-muted-foreground">mín. {a.minimo}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`entrada-${a.codigo}`}
                              onClick={() => setEntrada({ articulo: a, cantidad: "", costo_unitario: a.costo || "",
                                                          proveedor: a.proveedor || "", fecha: hoy(), documento: "",
                                                          registrar_gasto: true, metodo_pago: "efectivo" })}>
                        <PackagePlus className="mr-1 h-3 w-3" /> Entrada
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" data-testid={`ajuste-${a.codigo}`}
                              onClick={() => setAjuste({ articulo: a, existencia: a.existencia, motivo: "" })}>
                        <SlidersHorizontal className="mr-1 h-3 w-3" /> Ajustar
                      </Button>
                      <Button size="sm" variant="ghost" className="rounded-full" data-testid={`movimientos-${a.codigo}`}
                              onClick={() => verMovimientos(a)} title="Ver movimientos">
                        <History className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`editar-articulo-${a.codigo}`}
                              onClick={() => setForm({ ...a, costo: a.costo ?? "", talla: a.talla || "",
                                                       grado: a.grado || "", proveedor: a.proveedor || "",
                                                       notas: a.notas || "", codigo: a.codigo })}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => eliminar(a)}
                              data-testid={`eliminar-articulo-${a.codigo}`}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visibles.length === 0 && (
          <Empty testId="inventario-vacio">
            {articulos.length === 0
              ? "Aún no hay artículos. Cree el primero con “Nuevo artículo”."
              : "Ningún artículo coincide con la búsqueda."}
          </Empty>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-articulo">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
            <DialogDescription>
              {form?.id ? "La existencia solo cambia con entradas o ajustes." : "Uniformes por talla, libros por grado o materiales."}
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Código</Label>
                <Input value={form.codigo} placeholder="Se genera solo si lo deja vacío"
                       className="font-mono" data-testid="articulo-codigo"
                       onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <select value={form.categoria} data-testid="articulo-categoria"
                        onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  {(categorias.length ? categorias : ["Uniformes", "Libros", "Materiales", "Otros"])
                    .map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre</Label>
                <Input value={form.nombre} data-testid="articulo-nombre"
                       placeholder="Camisa polo blanca · Libro de Matemática · Cuaderno rayado"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Talla / variante</Label>
                <input list="tallas-inv" value={form.talla} data-testid="articulo-talla"
                       onChange={(e) => setForm({ ...form, talla: e.target.value })}
                       className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                <datalist id="tallas-inv">{TALLAS.map((t) => <option key={t} value={t} />)}</datalist>
              </div>
              <div className="space-y-2">
                <Label>Grado (para libros)</Label>
                <Input value={form.grado} data-testid="articulo-grado"
                       onChange={(e) => setForm({ ...form, grado: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Costo (RD$)</Label>
                <Input type="number" value={form.costo} data-testid="articulo-costo"
                       onChange={(e) => setForm({ ...form, costo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Precio de venta (RD$)</Label>
                <Input type="number" value={form.precio} data-testid="articulo-precio"
                       onChange={(e) => setForm({ ...form, precio: e.target.value })} />
              </div>
              {!form.id && (
                <div className="space-y-2">
                  <Label>Existencia inicial</Label>
                  <Input type="number" value={form.existencia_inicial} data-testid="articulo-existencia"
                         onChange={(e) => setForm({ ...form, existencia_inicial: e.target.value })} />
                </div>
              )}
              <div className="space-y-2">
                <Label>Mínimo antes de avisar</Label>
                <Input type="number" value={form.minimo} data-testid="articulo-minimo"
                       onChange={(e) => setForm({ ...form, minimo: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Proveedor</Label>
                <Input value={form.proveedor} data-testid="articulo-proveedor"
                       onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
              </div>
              {form.id && (
                <label className="flex items-center gap-3 text-sm sm:col-span-2">
                  <Checkbox checked={form.activo !== false} data-testid="articulo-activo"
                            onCheckedChange={(v) => setForm({ ...form, activo: !!v })} />
                  Disponible para la venta
                </label>
              )}
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardar} data-testid="guardar-articulo">
                {form.id ? "Guardar cambios" : "Agregar al inventario"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!entrada} onOpenChange={(o) => !o && setEntrada(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-entrada">
          <DialogHeader>
            <DialogTitle className="font-display">Entrada de mercancía</DialogTitle>
            <DialogDescription>
              {entrada?.articulo.nombre}{entrada?.articulo.talla ? ` · talla ${entrada.articulo.talla}` : ""}
              {" · existencia actual "}{entrada?.articulo.existencia}
            </DialogDescription>
          </DialogHeader>
          {entrada && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Cantidad recibida</Label>
                <Input type="number" min="1" value={entrada.cantidad} data-testid="entrada-cantidad"
                       onChange={(e) => setEntrada({ ...entrada, cantidad: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Costo por unidad (RD$)</Label>
                <Input type="number" value={entrada.costo_unitario} data-testid="entrada-costo"
                       onChange={(e) => setEntrada({ ...entrada, costo_unitario: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={entrada.fecha} data-testid="entrada-fecha"
                       onChange={(e) => setEntrada({ ...entrada, fecha: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Input value={entrada.proveedor} data-testid="entrada-proveedor"
                       onChange={(e) => setEntrada({ ...entrada, proveedor: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Factura o documento del proveedor</Label>
                <Input value={entrada.documento} data-testid="entrada-documento"
                       onChange={(e) => setEntrada({ ...entrada, documento: e.target.value })} />
              </div>
              <label className="flex items-start gap-3 text-sm sm:col-span-2">
                <Checkbox checked={entrada.registrar_gasto} data-testid="entrada-registrar-gasto"
                          onCheckedChange={(v) => setEntrada({ ...entrada, registrar_gasto: !!v })} />
                <span>
                  Registrar también el gasto
                  <span className="block text-xs text-muted-foreground">
                    Crea el gasto en la categoría “Inventario” por {money((Number(entrada.costo_unitario) || 0) * (Number(entrada.cantidad) || 0))}.
                  </span>
                </span>
              </label>
              {entrada.registrar_gasto && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Método de pago</Label>
                  <select value={entrada.metodo_pago} data-testid="entrada-metodo-pago"
                          onChange={(e) => setEntrada({ ...entrada, metodo_pago: e.target.value })}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                    {["efectivo", "transferencia", "tarjeta", "cheque"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardarEntrada} data-testid="guardar-entrada">
                Registrar entrada
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!ajuste} onOpenChange={(o) => !o && setAjuste(null)}>
        <DialogContent data-testid="modal-ajuste">
          <DialogHeader>
            <DialogTitle className="font-display">Ajustar existencia</DialogTitle>
            <DialogDescription>
              {ajuste?.articulo.nombre} · el sistema tiene {ajuste?.articulo.existencia}
            </DialogDescription>
          </DialogHeader>
          {ajuste && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Existencia real contada</Label>
                <Input type="number" value={ajuste.existencia} data-testid="ajuste-existencia"
                       onChange={(e) => setAjuste({ ...ajuste, existencia: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Input value={ajuste.motivo} data-testid="ajuste-motivo"
                       placeholder="Conteo físico, rotura, pérdida, corrección…"
                       onChange={(e) => setAjuste({ ...ajuste, motivo: e.target.value })} />
              </div>
              <Button className="rounded-full py-6" onClick={guardarAjuste} data-testid="guardar-ajuste">
                Guardar ajuste
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!movimientos} onOpenChange={(o) => !o && setMovimientos(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="modal-movimientos">
          <DialogHeader>
            <DialogTitle className="font-display">Movimientos · {movimientos?.articulo.nombre}</DialogTitle>
            <DialogDescription>Entradas, ventas y ajustes del artículo.</DialogDescription>
          </DialogHeader>
          <table className="w-full text-sm" data-testid="tabla-movimientos">
            <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="py-2">Fecha</th>
                <th className="py-2">Tipo</th>
                <th className="py-2 text-right">Cant.</th>
                <th className="py-2 text-right">Queda</th>
                <th className="py-2">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(movimientos?.rows || []).map((m) => (
                <tr key={m.id} className="border-t border-border/60">
                  <td className="py-2 font-mono text-xs">{fecha(m.fecha)}</td>
                  <td className="py-2">{m.tipo}</td>
                  <td className={`py-2 text-right font-mono ${m.cantidad < 0 ? "text-destructive" : "text-primary"}`}>
                    {m.tipo === "ajuste" ? "=" : m.cantidad > 0 ? `+${m.cantidad}` : m.cantidad}
                  </td>
                  <td className="py-2 text-right font-mono">{m.existencia_resultante}</td>
                  <td className="py-2 text-muted-foreground">{m.motivo || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(movimientos?.rows || []).length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Sin movimientos todavía.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
